import logger from "../services/logger";

// Liste des attributs traductibles (doit matcher backend + admin)
export const TRANSLATABLE_ATTRS = ["alt", "title", "placeholder", "aria-label"];

/**
 * Neutralise les unit\u00e9s viewport (vh, vw, vmin, vmax) dans le HTML d'un bloc IA.
 * Sans \u00e7a, un \u00e9l\u00e9ment avec min-height: 100vh dans une iframe avec auto-resize
 * cr\u00e9e une boucle infinie : iframe height \u2192 vh interne \u2192 contenu plus grand \u2192 etc.
 *
 * On remplace les vh/vmin/vmax par des valeurs proportionnelles bas\u00e9es sur la
 * hauteur typique d'un \u00e9cran (1px de vh = ~6.5px sur un \u00e9cran 650px de haut).
 * Pour un design destin\u00e9 \u00e0 \u00eatre embarqu\u00e9 dans une iframe, c'est un compromis
 * acceptable.
 */
export function stripViewportUnits(html) {
    if (!html || typeof html !== "string") return html;
    // Remplace 100vh par 80vh (basique fallback) → en fait on remplace par px
    // calcul\u00e9s : 1vh \u2248 6.5px (basis 650px de hauteur visible standard)
    return html
        .replace(/(\d+(?:\.\d+)?)vh\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`)
        .replace(/(\d+(?:\.\d+)?)vmin\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`)
        .replace(/(\d+(?:\.\d+)?)vmax\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`);
    // vw est conserv\u00e9 : la largeur de l'iframe ne d\u00e9pend pas de son contenu
}

/**
 * Applique un dict de traduction au HTML.
 * - Pour chaque [data-i18n="key"] : remplace innerHTML par dict[key]
 * - Pour chaque [data-i18n-<attr>="key"] : remplace l'attribut <attr> par dict[key]
 * - Si une cl\u00e9 manque, fallback sur fallbackDict (langue principale)
 *
 * Fonctionne uniquement c\u00f4t\u00e9 navigateur (utilise DOMParser).
 * Si DOMParser n'est pas dispo (SSR), retourne le HTML brut.
 */
export function applyAiI18n(html, dict, fallbackDict) {
    if (!html) return "";
    if (!dict && !fallbackDict) return html;
    if (typeof DOMParser === "undefined") return html;

    try {
        const isFullDoc = /^\s*(<!DOCTYPE|<html)/i.test(html);
        const parser = new DOMParser();

        if (isFullDoc) {
            // Document complet (ex: pages CMS avec doctype)
            const doc = parser.parseFromString(html, "text/html");
            applyToRoot(doc, dict, fallbackDict);
            return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
        } else {
            // Fragment (ex: blocs ai_content_blocks)
            const doc = parser.parseFromString(`<div id="__root">${html}</div>`, "text/html");
            const root = doc.getElementById("__root");
            if (!root) return html;
            applyToRoot(root, dict, fallbackDict);
            return root.innerHTML;
        }
    } catch (e) {
        logger.warn({ err: e }, "applyAiI18n error:");
        return html;
    }
}

function applyToRoot(root, dict, fallbackDict) {
    root.querySelectorAll("[data-i18n]").forEach((el) => {
        const key = el.getAttribute("data-i18n");
        const value = (dict && dict[key]) || (fallbackDict && fallbackDict[key]);
        if (value !== undefined && value !== null) {
            el.innerHTML = value;
        }
    });

    TRANSLATABLE_ATTRS.forEach((attr) => {
        root.querySelectorAll(`[data-i18n-${attr}]`).forEach((el) => {
            const key = el.getAttribute(`data-i18n-${attr}`);
            const value = (dict && dict[key]) || (fallbackDict && fallbackDict[key]);
            if (value !== undefined && value !== null) {
                el.setAttribute(attr, value);
            }
        });
    });
}
